import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isSectionHeaderLayout, readSlideMasters } from "@/office/powerpoint/design";
import { hasTitleText, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionGrosseDateien: Lesson = {
  id: "grosse-dateien",
  title: "Große Präsentationen im Griff behalten",
  summary: "Folien wiederverwenden, Dateigröße begrenzen, zu zweit daran arbeiten.",
  durationMinutes: 10,
  learningGoals: [
    "Folien aus anderen Präsentationen sauber übernehmen",
    "Die Dateigröße einer Präsentation begrenzen",
    "Eine lange Präsentation strukturell prüfen",
  ],
  steps: [
    {
      id: "folien-wiederverwenden",
      title: "Folien wiederverwenden",
      explanation: [
        "„Start“ → „Neue Folie“ → „Folien wiederverwenden“ öffnet einen Bereich, in dem " +
          "sich eine andere Präsentation auswählen und einzelne Folien daraus übernehmen " +
          "lassen. Das ist der saubere Weg, weil unten im Bereich das Häkchen " +
          "„Ursprüngliche Formatierung verwenden“ steht – standardmäßig nicht gesetzt.",
        "Nicht gesetzt heißt: Die Folie übernimmt Design und Folienmaster der " +
          "Zielpräsentation. Genau das will man fast immer. Gesetzt heißt: Der " +
          "Folienmaster der Quelldatei kommt mit, und die Präsentation hat danach zwei " +
          "Master – mit den Folgen aus dem Modul „Folienmaster und Design“.",
        "Nach dem Übernehmen lohnt sich ein Blick auf das Layout der neuen Folie. " +
          "Layouts werden über ihren Namen zugeordnet; heißt das Layout in der Quelldatei " +
          "anders, landet die Folie unter Umständen auf einem unpassenden Layout. „Start“ " +
          "→ „Layout“ setzt das in einem Klick gerade, „Zurücksetzen“ richtet die " +
          "Platzhalter danach aus.",
      ],
      keyPoints: [
        "Start → Neue Folie → Folien wiederverwenden.",
        "„Ursprüngliche Formatierung verwenden“ nicht anhaken.",
        "Nach der Übernahme Layout prüfen und zurücksetzen.",
      ],
    },
    {
      id: "dateigroesse",
      title: "Die Dateigröße",
      explanation: [
        "Eine Präsentation, die sich nicht verschicken lässt, ist ein praktisches " +
          "Problem. Die üblichen Grenzen liegen bei 20 bis 25 MB pro E-Mail; darüber " +
          "braucht es einen Link auf einen Speicherort.",
        "Die drei Ursachen für große Dateien sind fast immer dieselben. Erstens Bilder " +
          "in voller Kameraauflösung – dagegen hilft „Bilder komprimieren“ mit der " +
          "Einstellung „Alle Bilder im Dokument“ und 150 ppi. Zweitens eingebettete " +
          "Videos, die als Datei mit in die Präsentation wandern; hier hilft „Datei“ → " +
          "„Informationen“ → „Medien komprimieren“. Drittens eingebettete Schriftarten, " +
          "besonders mit der Option „Alle Zeichen einbetten“ statt „Nur die im " +
          "Dokument verwendeten Zeichen“.",
        "Ein oft übersehener Punkt sind zugeschnittene Bilder: Der abgeschnittene Teil " +
          "bleibt in der Datei, solange beim Komprimieren nicht das Häkchen " +
          "„Zugeschnittene Bildbereiche löschen“ gesetzt wird. Das ist nicht nur eine " +
          "Frage der Größe, sondern auch der Vertraulichkeit.",
        "Zum Prüfen: „Datei“ → „Informationen“ zeigt die Dateigröße. Wer wissen will, " +
          "was den Platz belegt, kann eine Kopie der .pptx-Datei in .zip umbenennen und " +
          "hineinschauen – eine PowerPoint-Datei ist ein Archiv.",
      ],
      keyPoints: [
        "Bildformat → Bilder komprimieren → Alle Bilder im Dokument, 150 ppi.",
        "Datei → Informationen → Medien komprimieren für eingebettete Videos.",
        "Eine .pptx ist ein Zip-Archiv – Umbenennen zeigt, was drinsteckt.",
      ],
    },
    {
      id: "strukturpruefung",
      title: "Strukturprüfung einer langen Präsentation",
      explanation: [
        "Bevor eine große Präsentation aus der Hand geht, lohnt sich eine kurze " +
          "Strukturprüfung. Sie beantwortet drei Fragen: Hat jede Folie einen Titel? " +
          "Gibt es Abschnittstrenner, die den Vortrag gliedern? Und arbeitet die Datei " +
          "mit genau einem Folienmaster?",
        "Alle drei Punkte sind nicht kosmetisch. Ohne Titel funktioniert weder die " +
          "Navigation noch der Screenreader. Ohne Trenner ist eine Präsentation über " +
          "zwanzig Folien für das Publikum ein einziger langer Block. Und mit zwei " +
          "Mastern lässt sich das Design nicht mehr in einem Zug ändern.",
      ],
      keyPoints: [
        "Gliederungsansicht für die Titel, Miniaturleiste für die Trenner.",
        "Ansicht → Folienmaster zeigt, wie viele Master es gibt.",
        "Diese drei Punkte kosten zwei Minuten und sparen später Stunden.",
      ],
      task: {
        instruction:
          "Prüfe deine Präsentation auf drei Punkte: Jede Folie hat einen Titel, es gibt " +
          "mindestens eine Trennfolie mit dem Layout „Abschnittsüberschrift“, und es " +
          "existiert genau ein Folienmaster.",
        hint: "Die drei Punkte lassen sich in Gliederungsansicht, Miniaturleiste und Folienmasteransicht ablesen.",
        check: async () => {
          const snapshot = await readPresentation();
          const master = await readSlideMasters();

          if (!snapshot.features.shapes || master === null) {
            return nichtPruefbar(
              "Layouts, Platzhalter und Folienmaster",
              "1.3",
              "Geh die drei Punkte selbst durch: Gliederungsansicht, Miniaturleiste, Folienmasteransicht.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an.",
            );
          }

          const ohneTitel = snapshot.slides.filter((folie) => !hasTitleText(folie));
          const trenner = snapshot.slides.filter((folie) =>
            isSectionHeaderLayout(folie.layoutType, folie.layoutName),
          );

          return fromCriteria(
            [
              criterion(
                ohneTitel.length === 0
                  ? `Alle ${snapshot.slides.length} Folien haben einen Titel`
                  : `Ohne Titel: Folie ${ohneTitel.map((folie) => folie.index + 1).join(", ")}`,
                ohneTitel.length === 0,
              ),
              criterion(
                trenner.length > 0
                  ? `Trennfolien vorhanden (${trenner.length})`
                  : "Mindestens eine Trennfolie mit dem Layout „Abschnittsüberschrift“",
                trenner.length > 0,
              ),
              criterion(
                `Genau ein Folienmaster (gefunden: ${master.length})`,
                master.length === 1,
              ),
            ],
            {
              success: "Die Präsentation ist auch bei wachsendem Umfang gut zu handhaben.",
              failure: "Bei der Struktur fehlt noch etwas:",
              hint: "„Start“ → „Zurücksetzen“ holt fehlende Titelplatzhalter zurück; überflüssige Folienmaster lassen sich in der Masteransicht per Rechtsklick löschen.",
            },
          );
        },
      },
    },
  ],
};
